import dayjs from 'dayjs'

/**
 * Storage primitives behind the dashboards' filter memory (DESIGN_GUIDE.md
 * section 7.11). React code goes through hooks/usePersistedSetState.ts and
 * hooks/useDateRangeSearchParams.ts; this module has no React so the session
 * teardown (common/session.ts) and impersonation can clear it.
 *
 * localStorage, so the memory survives closing the tab and the browser. Two
 * tabs on the same screen share one entry (last write wins; each tab restores
 * only on mount). Entries are keyed by user id and survive logout, so a user
 * finds their filters again on the next sign-in and never sees another
 * user's. Switching back to per-tab memory is the one-line change in
 * getStorage().
 */

const PREFIX = 'activate.filters.v1'

const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Storage access throws in some privacy modes; memory is then simply off.
    return null
  }
}

/** The user / group / building part of a key. Portfolio views pass no building. */
export const filterScope = (userId: string | undefined | null, groupId: string | undefined | null, building?: string | null) =>
  `${userId || 'anon'}:${groupId || '-'}:${building || 'portfolio'}`

export const filterStorageKey = (scope: string, screen: string) => `${PREFIX}:${scope}:${screen}`

/** Drops every remembered filter for every user on this browser. Not called in the normal flow (memory survives logout); kept for support tooling. */
export const clearPersistedFilters = () => {
  const storage = getStorage()
  if (!storage) return
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key && key.startsWith(`${PREFIX}:`)) doomed.push(key)
  }
  doomed.forEach((key) => storage.removeItem(key))
}

// Dayjs instances do not survive JSON. JSON.stringify calls toJSON before the
// replacer sees a value, so the walk happens up front instead.
const DAYJS_TAG = '__dayjs'
export const encode = (value: unknown): unknown => {
  if (dayjs.isDayjs(value)) return { [DAYJS_TAG]: value.toISOString() }
  if (Array.isArray(value)) return value.map(encode)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, encode(v)]))
  }
  return value
}
export const decode = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(decode)
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (typeof obj[DAYJS_TAG] === 'string') {
      const d = dayjs(obj[DAYJS_TAG] as string)
      return d.isValid() ? d : null
    }
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, decode(v)]))
  }
  return value
}

export const readEntry = (storageKey: string): Record<string, unknown> | null => {
  const storage = getStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(storageKey)
    if (!raw) return null
    const parsed = decode(JSON.parse(raw))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Writes an already-serialised entry (see encode). Best-effort: quota or privacy mode just skips. */
export const writeRaw = (storageKey: string, raw: string) => {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.setItem(storageKey, raw)
  } catch {
    // best-effort
  }
}

export const writeEntry = (storageKey: string, slice: Record<string, unknown>) => writeRaw(storageKey, JSON.stringify(encode(slice)))

export const removeEntry = (storageKey: string) => {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(storageKey)
  } catch {
    // best-effort
  }
}

/**
 * A stored value only replaces the default when it has the same shape. This
 * is what makes a renamed or retyped field harmless: the default wins and the
 * stale value is overwritten on the next write.
 */
export const sameShape = (stored: unknown, fallback: unknown) => {
  if (fallback === null || fallback === undefined) return true
  if (Array.isArray(fallback)) return stored === null || Array.isArray(stored)
  if (dayjs.isDayjs(fallback)) return stored === null || dayjs.isDayjs(stored)
  // typeof null is 'object': a null must not stand in for an object default.
  if (stored === null) return false
  return typeof stored === typeof fallback
}

/**
 * The remembered slice for a key, merged over the defaults: every persisted
 * key comes back (default or remembered), nothing else does.
 */
export const restoreSlice = <S extends Record<string, any>>(storageKey: string, base: S, persist: ReadonlyArray<keyof S>): Partial<S> => {
  const stored = readEntry(storageKey)
  const slice: Partial<S> = {}
  persist.forEach((k) => {
    slice[k] = base[k]
    if (!stored || !(k in stored)) return
    const value = stored[k as string]
    if (sameShape(value, base[k])) slice[k] = value as S[typeof k]
  })
  return slice
}

/**
 * Drops remembered ids that are no longer offered. Call it once the option
 * list has loaded, never before (an empty list would wipe the selection).
 * If nothing survives the pill goes back to All (null) rather than "none",
 * which would show an empty table for no visible reason.
 */
export const pruneIds = (ids: string[] | null | undefined, known: Iterable<string>): string[] | null => {
  if (ids === null || ids === undefined) return null
  const set = known instanceof Set ? known : new Set(known)
  const kept = ids.filter((id) => set.has(id))
  return kept.length ? kept : null
}

/** Single-select variant: an unknown remembered value falls back to the default. */
export const pruneValue = <T extends string | number>(value: T, known: Iterable<T>, fallback: T): T => {
  const set = known instanceof Set ? known : new Set(known)
  return set.has(value) ? value : fallback
}

/** Clamps a remembered page to the last page. `first` is 0 for TablePagination screens, 1 for TablePager ones. */
export const clampPage = (page: number, pageCount: number, first: 0 | 1 = 0) => {
  const last = first === 0 ? Math.max(0, pageCount - 1) : Math.max(1, pageCount)
  return Math.min(Math.max(first, page), last)
}
